import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { BasketItemOption, OptionQuantity } from '../../models/product-order.model';
import { StorageService } from '../storage.service';
import { PartnerHostnameProvider } from '../../providers/partner-hostname.provider';
import { ArrayUtils } from '../../shared/array-utils';
import { AvailabilityModel } from '../../providers/availability.provider';
import * as moment from 'moment';
import { map } from 'rxjs/operators';
import { API_ISO_DAY_TIME_FORMAT } from 'src/app/shared/date-utils';
import { PricingSchemas } from 'src/app/models/enums';
import { ProductTypeId } from 'src/app/models/enums/product-type.enum';

@Injectable()
export class CartService {
  static STORAGE_KEY = `cart_items-${PartnerHostnameProvider.hostname}`;

  private options: BasketItemOption[] = [];
  private optionsSubject: BehaviorSubject<BasketItemOption[]> = new BehaviorSubject<BasketItemOption[]>([]);

  constructor(private storage: StorageService) {
    const result = storage.get(CartService.STORAGE_KEY) as BasketItemOption[];
    const today = moment().startOf('day');

    this.options = result ? result.filter((item) => this.nonExpiredBasketItems(item, today)) : [];
    this.optionsSubject.next(this.options);
  }

  public addOption(option: BasketItemOption) {
    option.createdAt = moment().format(API_ISO_DAY_TIME_FORMAT);
    this.combineWithMatchingItemOption(option);
    this.storage.set(CartService.STORAGE_KEY, this.validOptions());
    this.optionsSubject.next(this.validOptions());
  }

  private combineWithMatchingItemOption(newBasketItem: BasketItemOption): void {
    const currentBasketItem = this.validOptions().find(
      (item) =>
        item.optionId == newBasketItem.optionId &&
        item.from == newBasketItem.from &&
        item.to == newBasketItem.to &&
        item.languageCode == newBasketItem.languageCode &&
        item.startTime == newBasketItem.startTime
    );

    let totalPrice = 0;

    if (currentBasketItem) {
      newBasketItem.optionQuantities.forEach((optionQuantity) => {
        let matchingOptionQuantity = currentBasketItem.optionQuantities.find(
          (item) => item.pricingSchemaId == optionQuantity.pricingSchemaId
        );

        if (matchingOptionQuantity) {
          matchingOptionQuantity.quantity += optionQuantity.quantity;
        } else {
          currentBasketItem.optionQuantities.push(optionQuantity);
          matchingOptionQuantity = optionQuantity;
        }
        totalPrice += matchingOptionQuantity.quantity * matchingOptionQuantity.price;
      });

      currentBasketItem.totalPrice = totalPrice;
    } else {
      this.options = [...this.validOptions(), newBasketItem];
    }
  }

  public removeOption(index: number) {
    this.options.splice(index, 1);
    this.sync();
  }

  public getList(): Observable<BasketItemOption[]> {
    const sixHoursAgo = moment().add(-6, 'hour');

    return this.optionsSubject
      .asObservable()
      .pipe(
        map((options: BasketItemOption[]) => options.filter((item) => this.nonExpiredBasketItems(item, sixHoursAgo)))
      );
  }

  public clearList() {
    this.options.length = 0;
    this.sync();
  }

  public remainingCapacity(availabilityRule: AvailabilityModel): number {
    const bookedOptions = this.validOptions()
      .filter((x) => x.startTime == availabilityRule.startTime && x.from == availabilityRule.day)
      .map((x) => x.optionQuantities.map((q) => q.quantity));
    const flattenOptions = bookedOptions.flat();

    return availabilityRule.availableCapacity - ArrayUtils.sum(flattenOptions);
  }

  private validOptions(): BasketItemOption[] {
    const sixHoursAgo = moment().add(-6, 'hour');
    return this.options.filter((item) => this.nonExpiredBasketItems(item, sixHoursAgo));
  }

  private nonExpiredBasketItems(item: BasketItemOption, sixHoursAgo: moment.Moment): boolean {
    return moment(item.createdAt).isSameOrAfter(sixHoursAgo);
  }

  private sync() {
    this.storage.set(CartService.STORAGE_KEY, this.validOptions());
    this.optionsSubject && this.optionsSubject.next([...this.validOptions()]);
  }

  getHotelPricingSchemasUi(basketItemOption: BasketItemOption) {
    if (basketItemOption.productTypeId !== ProductTypeId.hotel) return '';

    const pricingSchemasUi = basketItemOption.optionQuantities
      .filter((x) => x.quantity > 0 && x.pricingSchemaId !== PricingSchemas.ExtraBed)
      .map((x) => {
        return `${x.quantity.toString()} ${this.getSchemaName(x)}`;
      });

    return `(${pricingSchemasUi.join(', ')})`;
  }

  getSchemaName(optionQuantity: OptionQuantity) {
    if (optionQuantity.pricingSchemaId === PricingSchemas.Child) {
      return optionQuantity.quantity === 1 ? 'Child' : 'Children';
    }

    return optionQuantity.quantity === 1 ? optionQuantity.name : optionQuantity.name + 's';
  }
}
